/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Bars3Icon as MenuIcon, XMarkIcon as XIcon } from '@heroicons/react/24/outline';
/* MenuIcon and XIcon are already imported from @heroicons/react/24/outline */
import { colors } from '../config/colors';
import { useAuth } from '../context/AuthContext';
import LoginModal from './auth/LoginModal';

const Navigation = ({ onOpenOnlineBook, onOpenAdminDashboard, onOpenAccessBook }) => {
  const navigate = useNavigate();
  const [nav, setNav] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const { user, hasPurchased, logout } = useAuth();
  const location = useLocation();

  const handleClick = () => setNav(!nav)

  const handleClose = () => {
    setNav(false);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      // Handle logout error silently
    }
  };

  const handleAccessBook = () => {
    if (!user) {
      // User is not logged in, show login modal
      setShowLoginModal(true);
    } else if (hasPurchased) {
      // User is logged in and has paid, navigate directly to page flip reader
      navigate('/reader-pageflip');
    } else {
      // User is logged in but hasn't paid, show access options
      onOpenAccessBook();
    }
  };

  const handleLoginSuccess = () => {
    // Close login modal and check if user has paid
    setShowLoginModal(false);
    // The handleAccessBook logic will be re-evaluated after login
    // with updated user and hasPurchased state
    handleAccessBook();
  };
  // Check if we're on the home page
  const isHomePage = location.pathname === '/';

  return (
    <>
      <div className="w-screen h-[70px] z-20 fixed bg-white shadow-md">
        <div className='px-4 flex justify-between items-center w-full h-full max-w-[1400px] mx-auto gap-6'>
          <div className='flex items-center'>
            {/* <img src={logo} alt="Fijoli Logo" className='h-12 w-auto mr-4' /> */}
            <Link to="/" className="text-red-600 text-4xl font-bold font-poppins hidden lg:flex hover:text-red-700 transition-colors">
              FIJOLI
            </Link>
          </div>

          {/* Centered Fijoli Text for Mobile */}
          <div className='absolute left-1/2 transform -translate-x-1/2 lg:hidden'>
            <Link to="/" className="text-red-600 text-4xl font-bold font-poppins hover:text-red-700 transition-colors">
              FIJOLI
            </Link>
          </div>

          {/* Centered Navigation Menu */}
          <div className='hidden lg:flex items-center justify-center flex-1'>
            <ul className='flex items-center gap-6 xl:gap-10 whitespace-nowrap'>
              <li className='p-0 cursor-pointer transition-colors'>
                {isHomePage ? (
                  <a href="#home" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Home</a>
                ) : (
                  <Link to="/#home" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Home</Link>
                )}
              </li>
              <li className='p-0 cursor-pointer transition-colors'>
                {isHomePage ? (
                  <a href="#story" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Story</a>
                ) : (
                  <Link to="/#story" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Story</Link>
                )}
              </li>
              <li className='p-0 cursor-pointer transition-colors'>
                {isHomePage ? (
                  <a href="#why-fijoli" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Why Fijoli</a>
                ) : (
                  <Link to="/#why-fijoli" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Why Fijoli</Link>
                )}
              </li>
              <li className='p-0 cursor-pointer transition-colors'>
                {isHomePage ? (
                  <a href="#whats-in-book" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>What's in the Book</a>
                ) : (
                  <Link to="/#whats-in-book" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>What's in the Book</Link>
                )}
              </li>
              <li className='p-0 cursor-pointer transition-colors'>
                {isHomePage ? (
                  <a href="#who-should-read-fijoli" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Who Should Read</a>
                ) : (
                  <Link to="/#who-should-read-fijoli" className={`text-${colors.text.dark} hover:text-${colors.primary.main} font-lato`}>Who Should Read</Link>
                )}
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className='hidden lg:flex items-center gap-3 shrink-0'>
            {/* Early Bird offer: full badge on wide screens, compact tag on the button below xl */}
            {!hasPurchased && (
              <div className="hidden xl:block whitespace-nowrap bg-red-600 text-white px-3 py-2 rounded-lg text-xs font-semibold animate-highlight-blink text-center">
                <div className="animate-blink">Avail Early Bird offer</div>
              </div>
            )}
            <div className="relative">
              <button
                onClick={handleAccessBook}
                className={`flex items-center px-5 py-2.5 whitespace-nowrap rounded-full text-lg font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-poppins`}
              >
                <span className="text-base">{user && hasPurchased ? 'Read Book' : 'Access Book'}</span>
              </button>
              {!hasPurchased && (
                <span className="xl:hidden pointer-events-none absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-bold leading-none text-red-700 shadow animate-blink">
                  Early Bird offer
                </span>
              )}
            </div>
            {user ? (
              <>
                {/* Read Online button temporarily commented for production testing */}
                {/* 
              <a
                href="/reader"
                className={`flex items-center px-5 py-2.5 whitespace-nowrap rounded-full text-lg font-semibold bg-red-600 text-white hover:bg-red-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-poppins`}
              >
                <span className="text-base">Read Online</span>
              </a>
              */}
                <button
                  onClick={handleLogout}
                  className="flex items-center px-4 py-2.5 rounded-full text-lg font-semibold bg-gray-600 text-white hover:bg-gray-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1 font-poppins"
                >
                  <span className="text-base">Logout</span>
                </button>


              </>
            ) : (
              <>
                <a
                  href="https://wa.me/+919008023230"
                  className={`flex items-center px-5 py-2.5 whitespace-nowrap rounded-full text-lg font-semibold border border-red-600 text-${colors.text.light} hover:bg-${colors.primary.dark} transition-all transform hover:scale-105 shadow-lg hover:shadow-xl font-poppins`}
                >
                  <span className="text-base">Contact Us</span>
                </a>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className='lg:hidden' onClick={handleClick}>
            {!nav ? (
              <MenuIcon className={`w-6 text-${colors.text.dark}`} />
            ) : (
              <XIcon className={`w-6 text-${colors.text.dark}`} />
            )}
          </div>
        </div>

        {/* Mobile Access Book Button - Hidden, moved to floating button */}

        {/* Mobile Navigation */}
        <div className={!nav ? 'hidden' : 'absolute bg-white w-full top-[68px] lg:hidden shadow-lg z-20'}>
          {/* Nav Links */}
          <ul className="px-5 pt-3 pb-2 border-b border-gray-100">
            {[
              { label: 'Home', hash: '#home' },
              { label: 'Story', hash: '#story' },
              { label: 'Why Fijoli', hash: '#why-fijoli' },
              { label: "What's in the Book", hash: '#whats-in-book' },
              { label: 'Who Should Read', hash: '#who-should-read-fijoli' },
            ].map(({ label, hash }) => (
              <li key={hash} onClick={handleClose} className="py-2.5 border-b border-gray-50 last:border-0">
                {isHomePage ? (
                  <a href={hash} className="block text-gray-700 hover:text-red-600 font-medium text-sm transition-colors">{label}</a>
                ) : (
                  <Link to={`/${hash}`} className="block text-gray-700 hover:text-red-600 font-medium text-sm transition-colors">{label}</Link>
                )}
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="px-5 py-4 space-y-3">
            <button
              onClick={() => { handleClose(); handleAccessBook(); }}
              className="w-full py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-semibold text-sm transition-colors shadow-md active:scale-[0.98]"
            >
              {hasPurchased ? '📖 Read Book' : '🔓 Access Book'}
            </button>
            <a
              href="https://wa.me/+919008023230"
              onClick={handleClose}
              className="block w-full py-3.5 border border-red-600 text-red-600 hover:bg-red-50 rounded-2xl font-semibold text-sm text-center transition-colors active:scale-[0.98]"
            >
              Contact Us
            </a>
          </div>

          {/* User strip */}
          {user && (
            <div className="mx-5 mb-4 flex items-center gap-3 bg-gray-50 rounded-2xl px-4 py-3 border border-gray-100">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-sm shrink-0">
                {(user.name || user.phone || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{user.name || user.phone}</p>
                {hasPurchased && <p className="text-xs text-green-500 font-medium">✓ Access Active</p>}
              </div>
              <button
                onClick={() => { handleClose(); handleLogout(); }}
                className="text-xs text-gray-400 hover:text-red-500 font-medium transition-colors shrink-0"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}

export default Navigation;